import PageAnimator from '../../components/ui/PageAnimator';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  Send,
  Users,
  Settings,
  Activity,
  RefreshCcw,
  DownloadCloud,
  Plus,
  CheckCircle2,
  Building2,
  UserCheck,
  ShieldCheck,
  Clock
} from 'lucide-react';
import MiniLoader from '../../components/MiniLoader';
import Tooltip from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/Input';
import CustomSelect from '../../components/ui/CustomSelect';
import { Badge } from '../../components/ui/Badge';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useNotification } from '../../context/NotificationContext';
import {
  getBrevoAccount,
  getBrevoCampaigns,
  createBrevoCampaign,
  getBrevoLists,
  createBrevoList,
  syncBrevoContacts,
  sendBrevoTest
} from '../../features/tools/api/brevoAPI';
import api from '../../api';

export default function Brevo() {
  const { showToast } = useNotification();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Données réelles API
  const [account, setAccount] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);

  // États de synchronisation
  const [selectedListId, setSelectedListId] = useState('');
  const [syncSource, setSyncSource] = useState('all'); // 'all', 'leads', 'clients'

  // Modales
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  // Formulaires modales
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    listId: '',
    senderName: 'Eden Patrimoine',
    content: ''
  });

  const [newListName, setNewListName] = useState('');
  const [testEmailData, setTestEmailData] = useState({
    recipientEmail: '',
    subject: 'Test de délivrabilité Eden Patrimoine',
    content: ''
  });

  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accData, campData, listsData] = await Promise.all([
        getBrevoAccount().catch(() => null),
        getBrevoCampaigns().catch(() => ({ campaigns: [] })),
        getBrevoLists().catch(() => ({ lists: [] }))
      ]);

      setAccount(accData);
      setCampaigns(campData.campaigns || []);
      const loadedLists = listsData.lists || [];
      setLists(loadedLists);
      if (loadedLists.length > 0 && !selectedListId) {
        setSelectedListId(String(loadedLists[0].id));
      }
    } catch (error) {
      console.error("Erreur de chargement Brevo", error);
      showToast("Erreur lors du chargement des données Brevo.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Création de campagne
  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (!newCampaign.name.trim() || !newCampaign.subject.trim()) {
      showToast("Veuillez renseigner le nom et l'objet de la campagne.", "error");
      return;
    }

    setActionLoading(true);
    try {
      await createBrevoCampaign({
        name: newCampaign.name,
        subject: newCampaign.subject,
        senderName: newCampaign.senderName,
        senderEmail: account?.senderEmail || "no-reply@hipparchus.fr",
        listIds: newCampaign.listId ? [Number(newCampaign.listId)] : undefined,
        htmlContent: newCampaign.content ? `<div style="font-family:sans-serif;padding:20px;color:#1e293b;">${newCampaign.content.replace(/\n/g, '<br/>')}</div>` : undefined
      });

      showToast("Campagne créée avec succès dans Brevo !", "success");
      setIsCampaignModalOpen(false);
      setNewCampaign({ name: '', subject: '', listId: '', senderName: 'Eden Patrimoine', content: '' });
      fetchData();
    } catch (error) {
      showToast(error.response?.data?.error || "Échec de la création de la campagne.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Création de liste
  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) {
      showToast("Veuillez renseigner un nom de liste.", "error");
      return;
    }

    setActionLoading(true);
    try {
      await createBrevoList({ name: newListName.trim() });
      showToast("Liste de contacts créée avec succès !", "success");
      setIsListModalOpen(false);
      setNewListName('');
      fetchData();
    } catch (error) {
      showToast(error.response?.data?.error || "Échec de la création de la liste.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Envoi email de test
  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testEmailData.recipientEmail.trim()) {
      showToast("Veuillez renseigner une adresse email valide.", "error");
      return;
    }

    setActionLoading(true);
    try {
      await sendBrevoTest(testEmailData);
      showToast("Email de test envoyé avec succès via Brevo !", "success");
      setIsTestModalOpen(false);
      setTestEmailData({ recipientEmail: '', subject: 'Test de délivrabilité Eden Patrimoine', content: '' });
    } catch (error) {
      showToast(error.response?.data?.error || "Échec de l'envoi de l'email de test.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Synchronisation des contacts CRM
  const handleSync = async () => {
    if (!selectedListId) {
      showToast("Veuillez sélectionner une liste Brevo de destination.", "error");
      return;
    }

    setSyncing(true);
    try {
      let contactsToSync = [];

      // 1. Récupération des prospects & leads
      if (syncSource === 'all' || syncSource === 'leads') {
        const leadsRes = await api.get('/leads').catch(() => ({ data: [] }));
        const rawLeads = leadsRes.data || [];
        rawLeads.forEach(l => {
          const email = l.email || l.emailAddress;
          if (email && email.includes('@')) {
            contactsToSync.push({
              email: email.trim().toLowerCase(),
              attributes: {
                PRENOM: l.firstName || l.prenom || '',
                NOM: l.lastName || l.nom || l.organizationName || 'Lead CRM'
              }
            });
          }
        });
      }

      // 2. Récupération des clients particuliers (familles)
      if (syncSource === 'all' || syncSource === 'clients') {
        const persRes = await api.get('/personnes').catch(() => ({ data: [] }));
        const rawPers = persRes.data || [];
        rawPers.forEach(p => {
          if (p.email && p.email.includes('@')) {
            contactsToSync.push({
              email: p.email.trim().toLowerCase(),
              attributes: {
                PRENOM: p.prenom || '',
                NOM: p.nom || ''
              }
            });
          }
        });
      }

      // Dédoublonnage par email
      const uniqueMap = new Map();
      contactsToSync.forEach(c => uniqueMap.set(c.email, c));
      const uniqueContacts = Array.from(uniqueMap.values());

      if (uniqueContacts.length === 0) {
        showToast("Aucun contact avec un email valide n'a été trouvé dans le CRM pour ce critère.", "warning");
        setSyncing(false);
        return;
      }

      const res = await syncBrevoContacts(selectedListId, uniqueContacts);
      showToast(res.message || `${uniqueContacts.length} contacts synchronisés avec succès !`, "success");
      fetchData();
    } catch (error) {
      console.error("Erreur synchronisation:", error);
      showToast("La synchronisation a rencontré une erreur.", "error");
    } finally {
      setSyncing(false);
    }
  };

  const tabs = [
    { id: 'campaigns', label: 'Campagnes Email', icon: Send, badge: campaigns.length },
    { id: 'lists', label: 'Listes & Synchronisation CRM', icon: Users, badge: lists.length },
    { id: 'settings', label: 'Configuration & Quotas', icon: Settings }
  ];

  const totalSubscribers = lists.reduce((acc, l) => acc + (l.uniqueSubscribers || 0), 0);
  const creditsRemaining = account?.plan?.[0]?.credits !== undefined ? account.plan[0].credits : 300;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-workspace">
        <MiniLoader />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen pb-24">
      <PageAnimator>
        {/* Header Premium Standardisé */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-4 sm:p-6
            mb-6
            xl:flex-row xl:items-center xl:justify-between
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <Mail className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                Marketing Automation (Brevo)
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Gérez vos communications client, campagnes d'information et listes de diffusion en temps réel.
              </p>
            </div>
          </div>

          <div className="relative z-10 flex flex-wrap items-center gap-3">
            <Badge variant={account?.connected !== false ? "success" : "warning"} className="py-1 px-3 shadow-hp-sm">
              <div className="w-2 h-2 rounded-full bg-[var(--hp-success-600)] animate-pulse mr-1" />
              {account?.connected !== false ? "API v3 Connectée" : "Mode Dégradé"}
            </Badge>

            <span className="text-xs px-3 py-1.5 rounded-full bg-surface text-primary border border-workspace font-semibold shadow-hp-sm flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[var(--hp-color-accent)]" />
              {creditsRemaining} crédits restants
            </span>

            <Tooltip content="Actualiser les données">
              <Button
                variant="secondary"
                size="icon"
                onClick={fetchData}
                className="shadow-hp-sm"
              >
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Navigation par Onglets Framer Motion */}
        <div className="flex gap-2 mb-6 border-b border-workspace pb-2 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-[var(--hp-radius-md)] text-sm font-semibold transition-all ${
                  isActive ? 'text-primary' : 'text-subtle hover:text-primary hover:bg-surface-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.badge !== undefined && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-secondary text-inverse' : 'bg-surface-muted text-subtle'}`}>
                    {tab.badge}
                  </span>
                )}
                {isActive && (
                  <motion.div
                    layoutId="brevoActiveTabIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--hp-color-accent)]"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Contenu de l'onglet actif */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
          >
            {/* ONGLET 1 : CAMPAGNES */}
            {activeTab === 'campaigns' && (
              <div className="space-y-6">
                {/* KPIs Campagnes */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="workspace-card p-5 bg-surface shadow-hp-sm border-workspace">
                    <p className="text-xs font-semibold text-subtle uppercase">Total Campagnes</p>
                    <p className="text-2xl font-bold text-primary mt-1">{campaigns.length}</p>
                    <p className="text-xs text-subtle mt-1">Toutes communications confondues</p>
                  </div>
                  <div className="workspace-card p-5 bg-surface shadow-hp-sm border-workspace">
                    <p className="text-xs font-semibold text-subtle uppercase">Abonnés Qualifiés</p>
                    <p className="text-2xl font-bold text-[var(--hp-color-accent)] mt-1">{totalSubscribers}</p>
                    <p className="text-xs text-subtle mt-1">Répartis sur {lists.length} liste(s)</p>
                  </div>
                  <div className="workspace-card p-5 bg-surface shadow-hp-sm border-workspace">
                    <p className="text-xs font-semibold text-subtle uppercase">Quota Journalier</p>
                    <p className="text-2xl font-bold text-[var(--hp-success-700)] mt-1">{creditsRemaining} / 300</p>
                    <p className="text-xs text-subtle mt-1">Renouvelé chaque jour à minuit</p>
                  </div>
                  <div className="workspace-card p-5 bg-surface shadow-hp-sm border-workspace">
                    <p className="text-xs font-semibold text-subtle uppercase">Expéditeur Vérifié</p>
                    <p className="text-sm font-bold text-primary mt-2 truncate" title={account?.senderEmail || "no-reply@hipparchus.fr"}>
                      {account?.senderEmail || "no-reply@hipparchus.fr"}
                    </p>
                    <Badge variant="success" className="mt-2 text-[10px]">Certifié DKIM / SPF</Badge>
                  </div>
                </div>

                {/* Actions et Liste des Campagnes */}
                <div className="workspace-card-elevated p-6 bg-surface">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-workspace">
                    <div>
                      <h3 className="text-lg font-bold text-primary">Campagnes Récentes</h3>
                      <p className="text-sm text-subtle">Suivez les performances de délivrabilité et d'engagement de vos envois.</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsTestModalOpen(true)}
                        className="gap-1.5"
                      >
                        <Send className="w-4 h-4" />
                        Envoyer un test
                      </Button>
                      <Button
                        variant="accent"
                        size="sm"
                        onClick={() => setIsCampaignModalOpen(true)}
                        className="gap-1.5 shadow-hp-sm"
                      >
                        <Plus className="w-4 h-4" />
                        Nouvelle Campagne
                      </Button>
                    </div>
                  </div>

                  {campaigns.length === 0 ? (
                    <div className="text-center py-16 text-subtle flex flex-col items-center justify-center">
                      <div className="h-16 w-16 rounded-full bg-surface-muted flex items-center justify-center mb-4 text-tertiary">
                        <Mail className="w-8 h-8" />
                      </div>
                      <p className="text-lg font-semibold text-primary">Aucune campagne enregistrée</p>
                      <p className="text-sm text-subtle mt-1 max-w-md">
                        Créez votre première communication pour informer vos clients des opportunités patrimoniales et évolutions fiscales.
                      </p>
                      <Button
                        variant="accent"
                        size="sm"
                        onClick={() => setIsCampaignModalOpen(true)}
                        className="mt-6 gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Créer une campagne
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {campaigns.map((camp) => {
                        const isSent = camp.status === 'sent';
                        const isDraft = camp.status === 'draft';
                        const stats = camp.statistics?.globalStats;

                        return (
                          <div
                            key={camp.id}
                            className="flex flex-col lg:flex-row lg:items-center justify-between p-4 border border-workspace rounded-[var(--hp-radius-md)] bg-surface hover:bg-surface-muted transition-colors gap-4"
                          >
                            <div className="flex items-start gap-3 min-w-0">
                              <div className={`p-2.5 rounded-full shrink-0 ${isSent ? 'bg-[var(--hp-success-100)] text-[var(--hp-success-700)]' : 'bg-[var(--hp-warning-100)] text-[var(--hp-warning-700)]'}`}>
                                <Mail className="w-5 h-5" />
                              </div>
                              <div className="min-w-0">
                                <h4 className="font-bold text-primary text-sm truncate">{camp.name}</h4>
                                <p className="text-xs text-subtle truncate mt-0.5">Objet : {camp.subject || "Non défini"}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant={isSent ? "success" : isDraft ? "warning" : "info"}>
                                    {isSent ? "Envoyée" : isDraft ? "Brouillon" : camp.status}
                                  </Badge>
                                  {camp.sentDate && (
                                    <span className="text-xs text-subtle flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {new Date(camp.sentDate).toLocaleDateString('fr-FR')} à {new Date(camp.sentDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {stats && (
                              <div className="flex items-center gap-6 text-sm text-subtle shrink-0 pt-3 lg:pt-0 border-t lg:border-t-0 border-workspace">
                                <div className="text-right">
                                  <p className="font-bold text-primary">{stats.sent || 0}</p>
                                  <p className="text-[11px] text-subtle">Envoyés</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-[var(--hp-success-700)]">{stats.opened || 0}</p>
                                  <p className="text-[11px] text-subtle">Ouvertures</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-[var(--hp-color-accent)]">{stats.clicked || 0}</p>
                                  <p className="text-[11px] text-subtle">Clics</p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ONGLET 2 : LISTES & SYNCHRONISATION CRM */}
            {activeTab === 'lists' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Colonne Gauche : Listes existantes (5 cols) */}
                <div className="lg:col-span-5 workspace-card-elevated p-6 bg-surface flex flex-col">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-workspace">
                    <div>
                      <h3 className="text-lg font-bold text-primary">Listes Brevo</h3>
                      <p className="text-xs text-subtle">Vos segments de destinataires synchronisés</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsListModalOpen(true)}
                      className="gap-1 text-xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Créer
                    </Button>
                  </div>

                  <div className="space-y-2.5 flex-1">
                    {lists.length === 0 ? (
                      <p className="text-sm text-subtle text-center py-8">Aucune liste disponible.</p>
                    ) : (
                      lists.map((l) => (
                        <div
                          key={l.id}
                          onClick={() => setSelectedListId(String(l.id))}
                          className={`flex items-center justify-between p-3.5 border rounded-[var(--hp-radius-md)] cursor-pointer transition-all ${
                            selectedListId === String(l.id)
                              ? 'border-[var(--hp-color-accent)] bg-surface-muted shadow-hp-sm'
                              : 'border-workspace bg-surface hover:bg-surface-muted'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Users className="w-4 h-4 text-secondary" />
                            <div>
                              <p className="text-sm font-semibold text-primary">{l.name}</p>
                              <p className="text-[11px] text-subtle">ID Brevo : #{l.id}</p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="font-mono">
                            {l.uniqueSubscribers || 0} contacts
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Colonne Droite : Moteur de Synchronisation CRM (7 cols) */}
                <div className="lg:col-span-7 workspace-card-elevated p-6 bg-surface border-l-4 border-[var(--hp-color-accent)]">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-[var(--hp-bronze-100)] text-[var(--hp-bronze-700)]">
                      <DownloadCloud className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-primary">Synchroniser les Contacts du CRM</h3>
                      <p className="text-xs text-subtle">
                        Alimentez automatiquement vos listes Brevo avec les coordonnées à jour de vos prospects et clients.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-5">
                    {/* Choix de la source */}
                    <div>
                      <label className="block text-xs font-bold text-primary uppercase tracking-wider mb-2">
                        1. Source des contacts à exporter :
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => setSyncSource('all')}
                          className={`p-3 rounded-[var(--hp-radius-md)] border text-left text-xs font-semibold transition-all ${
                            syncSource === 'all'
                              ? 'border-[var(--hp-color-accent)] bg-[var(--hp-bronze-100)] text-[var(--hp-bronze-700)] shadow-hp-sm'
                              : 'border-workspace bg-surface text-subtle hover:bg-surface-muted'
                          }`}
                        >
                          <Users className="w-4 h-4 mb-1" />
                          Tous les contacts
                        </button>
                        <button
                          type="button"
                          onClick={() => setSyncSource('leads')}
                          className={`p-3 rounded-[var(--hp-radius-md)] border text-left text-xs font-semibold transition-all ${
                            syncSource === 'leads'
                              ? 'border-[var(--hp-color-accent)] bg-[var(--hp-bronze-100)] text-[var(--hp-bronze-700)] shadow-hp-sm'
                              : 'border-workspace bg-surface text-subtle hover:bg-surface-muted'
                          }`}
                        >
                          <UserCheck className="w-4 h-4 mb-1" />
                          Leads & Prospects
                        </button>
                        <button
                          type="button"
                          onClick={() => setSyncSource('clients')}
                          className={`p-3 rounded-[var(--hp-radius-md)] border text-left text-xs font-semibold transition-all ${
                            syncSource === 'clients'
                              ? 'border-[var(--hp-color-accent)] bg-[var(--hp-bronze-100)] text-[var(--hp-bronze-700)] shadow-hp-sm'
                              : 'border-workspace bg-surface text-subtle hover:bg-surface-muted'
                          }`}
                        >
                          <Building2 className="w-4 h-4 mb-1" />
                          Clients & Familles
                        </button>
                      </div>
                    </div>

                    {/* Sélection de la liste de destination */}
                    <div>
                      <label className="block text-xs font-bold text-primary uppercase tracking-wider mb-2">
                        2. Liste Brevo de destination :
                      </label>
                      <CustomSelect
                        value={selectedListId}
                        onChange={(val) => setSelectedListId(val)}
                        options={lists.map(l => ({
                          value: String(l.id),
                          label: `${l.name} (${l.uniqueSubscribers || 0} abonnés existants)`
                        }))}
                        placeholder="Sélectionnez une liste de diffusion..."
                      />
                    </div>

                    {/* Explications et sécurité */}
                    <div className="p-3.5 bg-surface-muted rounded-[var(--hp-radius-md)] border border-workspace text-xs text-subtle space-y-1">
                      <p className="font-semibold text-primary flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[var(--hp-success-600)]" />
                        Mise à jour incrémentale sécurisée
                      </p>
                      <p>
                        Les contacts déjà présents dans la liste seront automatiquement actualisés sans générer de doublons. Seuls les emails valides sont synchronisés.
                      </p>
                    </div>

                    {/* Bouton de synchronisation */}
                    <Button
                      variant="accent"
                      onClick={handleSync}
                      disabled={!selectedListId || syncing}
                      className="w-full shadow-hp-sm py-3 font-semibold"
                    >
                      {syncing ? (
                        <>
                          <Activity className="w-4 h-4 animate-spin mr-2" />
                          Synchronisation des contacts en cours...
                        </>
                      ) : (
                        <>
                          <RefreshCcw className="w-4 h-4 mr-2" />
                          Lancer la synchronisation vers Brevo
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET 3 : CONFIGURATION & QUOTAS */}
            {activeTab === 'settings' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="workspace-card-elevated p-6 bg-surface">
                  <h3 className="text-lg font-bold text-primary mb-4 pb-2 border-b border-workspace">
                    Identité du Compte Brevo
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-subtle uppercase mb-1">Raison Sociale</label>
                      <p className="text-sm font-semibold text-primary">{account?.companyName || "EDEN PATRIMOINE"}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-subtle uppercase mb-1">Email Administrateur</label>
                      <p className="text-sm font-semibold text-primary">{account?.email || "baptiste.dehay@edenpatrimoine.fr"}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-subtle uppercase mb-1">Expéditeur Certifié (Sender)</label>
                      <p className="text-sm font-semibold text-primary">{account?.senderEmail || "no-reply@hipparchus.fr"}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-subtle uppercase mb-1">Plan & Licences</label>
                      <Badge variant="accent" className="font-medium">
                        Brevo Business / Free Tier (300 emails/jour)
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="workspace-card-elevated p-6 bg-surface">
                  <h3 className="text-lg font-bold text-primary mb-4 pb-2 border-b border-workspace">
                    Sécurité & Connecteur API
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-subtle uppercase mb-1">Authentification</label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="password"
                          value="••••••••••••••••••••••••••••••••••••••••"
                          disabled
                          className="bg-surface-muted text-xs"
                        />
                        <Badge variant="success">Active</Badge>
                      </div>
                      <p className="text-[11px] text-subtle mt-1">La clé secrète v3 est stockée de manière chiffrée côté serveur.</p>
                    </div>

                    <div className="p-4 bg-surface-muted rounded-[var(--hp-radius-md)] border border-workspace">
                      <h4 className="text-xs font-bold text-primary uppercase mb-1">Délivrabilité & Conformité RGPD</h4>
                      <p className="text-xs text-subtle">
                        Les liens de désinscription automatique et les mentions légales obligatoires sont injectés dans toutes vos campagnes par le moteur Brevo.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* MODALE 1 : NOUVELLE CAMPAGNE */}
        <Dialog open={isCampaignModalOpen} onClose={() => !actionLoading && setIsCampaignModalOpen(false)} className="relative z-50">
          <DialogBackdrop className="fixed inset-0 bg-ink-500/75 backdrop-blur-sm" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <DialogPanel className="bg-surface rounded-xl shadow-xl w-full max-w-lg border border-workspace overflow-hidden">
              <form onSubmit={handleCreateCampaign}>
                <div className="p-6 border-b border-workspace">
                  <DialogTitle as="h3" className="text-lg font-bold text-primary flex items-center gap-2">
                    <Send className="w-5 h-5 text-[var(--hp-color-accent)]" />
                    Créer une Campagne Email
                  </DialogTitle>
                  <p className="text-xs text-subtle mt-1">Configurez une nouvelle communication pour vos contacts.</p>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-primary uppercase mb-1.5">Nom interne de la campagne *</label>
                    <Input
                      placeholder="Ex: Newsletter Patrimoine - Mars 2026"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-primary uppercase mb-1.5">Objet de l'email (reçu par le client) *</label>
                    <Input
                      placeholder="Ex: Opportunités d'investissement et actualités fiscales"
                      value={newCampaign.subject}
                      onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-primary uppercase mb-1.5">Liste de destinataires</label>
                    <CustomSelect
                      value={newCampaign.listId}
                      onChange={(val) => setNewCampaign({ ...newCampaign, listId: val })}
                      options={lists.map(l => ({ value: String(l.id), label: `${l.name} (${l.uniqueSubscribers || 0} abonnés)` }))}
                      placeholder="Toutes ou sélectionner une liste..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-primary uppercase mb-1.5">Message / Contenu texte</label>
                    <textarea
                      rows={4}
                      className="workspace-input w-full text-sm p-3"
                      placeholder="Saisissez le corps de votre message..."
                      value={newCampaign.content}
                      onChange={(e) => setNewCampaign({ ...newCampaign, content: e.target.value })}
                    />
                  </div>
                </div>

                <div className="bg-workspace px-6 py-4 flex justify-end gap-3 border-t border-workspace">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setIsCampaignModalOpen(false)}
                    disabled={actionLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="accent"
                    type="submit"
                    disabled={actionLoading}
                    className="shadow-hp-sm"
                  >
                    {actionLoading ? "Création..." : "Créer dans Brevo"}
                  </Button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </Dialog>

        {/* MODALE 2 : NOUVELLE LISTE */}
        <Dialog open={isListModalOpen} onClose={() => !actionLoading && setIsListModalOpen(false)} className="relative z-50">
          <DialogBackdrop className="fixed inset-0 bg-ink-500/75 backdrop-blur-sm" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <DialogPanel className="bg-surface rounded-xl shadow-xl w-full max-w-md border border-workspace overflow-hidden">
              <form onSubmit={handleCreateList}>
                <div className="p-6 border-b border-workspace">
                  <DialogTitle as="h3" className="text-lg font-bold text-primary flex items-center gap-2">
                    <Users className="w-5 h-5 text-[var(--hp-color-accent)]" />
                    Nouvelle Liste de Contacts
                  </DialogTitle>
                </div>

                <div className="p-6">
                  <label className="block text-xs font-bold text-primary uppercase mb-1.5">Nom de la liste *</label>
                  <Input
                    placeholder="Ex: Investisseurs SCPI 2026"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    required
                  />
                </div>

                <div className="bg-workspace px-6 py-4 flex justify-end gap-3 border-t border-workspace">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setIsListModalOpen(false)}
                    disabled={actionLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="accent"
                    type="submit"
                    disabled={actionLoading}
                    className="shadow-hp-sm"
                  >
                    {actionLoading ? "Création..." : "Créer la liste"}
                  </Button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </Dialog>

        {/* MODALE 3 : ENVOI DE TEST */}
        <Dialog open={isTestModalOpen} onClose={() => !actionLoading && setIsTestModalOpen(false)} className="relative z-50">
          <DialogBackdrop className="fixed inset-0 bg-ink-500/75 backdrop-blur-sm" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <DialogPanel className="bg-surface rounded-xl shadow-xl w-full max-w-md border border-workspace overflow-hidden">
              <form onSubmit={handleSendTest}>
                <div className="p-6 border-b border-workspace">
                  <DialogTitle as="h3" className="text-lg font-bold text-primary flex items-center gap-2">
                    <Send className="w-5 h-5 text-[var(--hp-color-accent)]" />
                    Envoyer un Email de Test
                  </DialogTitle>
                  <p className="text-xs text-subtle mt-1">Vérifiez la bonne réception et délivrabilité sur votre boîte mail.</p>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-primary uppercase mb-1.5">Email destinataire *</label>
                    <Input
                      type="email"
                      placeholder="nom@cabinet.fr"
                      value={testEmailData.recipientEmail}
                      onChange={(e) => setTestEmailData({ ...testEmailData, recipientEmail: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-primary uppercase mb-1.5">Objet du test</label>
                    <Input
                      value={testEmailData.subject}
                      onChange={(e) => setTestEmailData({ ...testEmailData, subject: e.target.value })}
                    />
                  </div>
                </div>

                <div className="bg-workspace px-6 py-4 flex justify-end gap-3 border-t border-workspace">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setIsTestModalOpen(false)}
                    disabled={actionLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="accent"
                    type="submit"
                    disabled={actionLoading}
                    className="shadow-hp-sm"
                  >
                    {actionLoading ? "Envoi..." : "Envoyer"}
                  </Button>
                </div>
              </form>
            </DialogPanel>
          </div>
        </Dialog>
      </PageAnimator>
    </div>
  );
}

